import { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoCloseOutline } from 'react-icons/io5';
import { 
  PRODUCTS, 
  HOME_SECTIONS, 
  UTILITIES, 
  getPageContext,
  DEFAULT_CONFIG 
} from './navigationConfig';
import { DirectHomeItem, ExpandableHomeItem, NavItem } from './MobileMenuItems.jsx';
import './MobileMenu.css';
const MobileMenu = ({ 
  isOpen, 
  onClose, 
  expandHomeSections = DEFAULT_CONFIG.expandHomeSections,
  showGroupHeaders = DEFAULT_CONFIG.showGroupHeaders
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [homeExpanded, setHomeExpanded] = useState(false);
  
  // Get current page context
  const pageContext = useMemo(() => 
    getPageContext(location.pathname), 
    [location.pathname]
  );
  
  const { pageType, currentId } = pageContext;
  const isHomePage = pageType === 'home';
  
  // Navigation handlers
  const handleNavigate = useCallback((path) => {
    navigate(path);
    onClose();
  }, [navigate, onClose]);
  
  const handleAnchorClick = useCallback((anchor) => {
    onClose();
    // If not on homepage, navigate first then scroll
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      setTimeout(() => {
        document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [isHomePage, navigate, onClose]);
  
  const handleHomeToggle = useCallback(() => {
    setHomeExpanded(prev => !prev);
  }, []);
  
  const handleNavigateHome = useCallback(() => {
    handleNavigate('/');
  }, [handleNavigate]);
  
  // Don't render if not open
  if (!isOpen) return null;
  
  return (
    <>
      {/* Overlay */}
      <div 
        className="mobile-menu-overlay" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Menu Panel */}
      <div className="mobile-menu" role="dialog" aria-label="Mobile navigation menu">
        {/* Header */}
        <div className="mobile-menu-header">
          <h2 className="mobile-menu-title">Menu</h2>
          <button
            className="mobile-menu-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <IoCloseOutline />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mobile-menu-nav">
          
          {/* Home Section */}
          {expandHomeSections ? (
            <ExpandableHomeItem
              isExpanded={homeExpanded}
              onToggle={handleHomeToggle}
              onNavigateHome={handleNavigateHome}
              onNavigateAnchor={handleAnchorClick}
              isHomePage={isHomePage}
            />
          ) : (
            <DirectHomeItem
              onClick={handleNavigateHome}
              isHomePage={isHomePage}
            />
          )}
          
          {/* Homepage Anchor Links (only shown on homepage in non-expandable mode) */}
          {isHomePage && !expandHomeSections && (
            <div className="mobile-menu-section">
              {HOME_SECTIONS.map(section => (
                <button 
                  key={section.id}
                  className="mobile-menu-item sub-item"
                  onClick={() => handleAnchorClick(section.anchor)}
                >
                  <span className="mobile-menu-label">{section.label}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Divider */}
          <div className="mobile-menu-divider" />
          
          {/* Primary Routes Group */}
          {showGroupHeaders && (
            <div className="mobile-menu-group-header">Explore</div>
          )}
          <div className="mobile-menu-section products">
            {PRODUCTS.map(product => (
              <NavItem
                key={product.id}
                item={product}
                isCurrent={currentId === product.id}
                onClick={() => handleNavigate(product.path)}
                hasIcon={true}
              />
            ))}
          </div>
          
          {/* Divider */}
          <div className="mobile-menu-divider" />
          
          {/* Contact Routes Group */}
          {showGroupHeaders && (
            <div className="mobile-menu-group-header">Connect</div>
          )}
          <div className="mobile-menu-section utilities">
            {UTILITIES.map(utility => (
              <NavItem
                key={utility.id}
                item={utility}
                isCurrent={currentId === utility.id}
                onClick={() => handleNavigate(utility.path)}
                hasIcon={true}
              />
            ))}
          </div>
          
        </nav>
      </div>
    </>
  );
};

export default MobileMenu;
